import { Spacing } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { ReactChild, useState } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import { NavbarIcon, NavbarItemContainer } from 'dashboard/components/Navbar/NavbarStyles';
import { NavItem } from 'dashboard/models/role';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

export interface EditModeNavbarItemData {
  navItemRedesign: NavItem;
  label: ReactChild;
  links: NavbarLinkData[];
}

export interface NavbarLinkData {
  label: ReactChild;
  navItemRedesign: NavItem;
}

export interface EditModeNavbarItemProps {
  item: EditModeNavbarItemData;
  links: NavbarLinkData[];
  userHiddenIds: NavItem[];
  setUserHiddenIds: (ids: NavItem[]) => void;
}

const EditModeNavbarItem = ({
  item,
  links,
  userHiddenIds,
  setUserHiddenIds,
}: EditModeNavbarItemProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleClick = ({ navItem, header = false }: { navItem: NavItem; header?: boolean }) => {
    if (userHiddenIds.includes(navItem)) {
      setUserHiddenIds(
        userHiddenIds.filter((hiddenNavItem) => {
          return (
            hiddenNavItem !== navItem &&
            (!header || !links.map((l) => l.navItemRedesign).includes(hiddenNavItem))
          );
        }),
      );
    } else {
      setUserHiddenIds([...userHiddenIds, navItem]);
    }
  };

  const Item = ({
    navItem,
    label,
    indent,
    header = false,
    sectionHidden = false,
  }: {
    navItem: NavItem;
    header?: boolean;
    label: ReactChild;
    indent: number;
    sectionHidden?: boolean;
  }) => {
    const isHiddenByUser = userHiddenIds.includes(navItem);
    const colorVariant = isHiddenByUser || sectionHidden ? 'disabled' : 'primary';

    return (
      <NavbarItemContainer colorVariant={colorVariant}>
        <Spacer width={indent} height={0} />
        <StyledLabelContainer>
          <Paragraph color='primary' size='sm'>
            {label}
          </Paragraph>
        </StyledLabelContainer>
        <Spacer width={1} height={0} />
        <NavbarIcon
          id={`navbar-edit__button--${navItem}`}
          colorVariant={colorVariant}
          onClick={() => handleClick({ navItem, header })}
        >
          {isHiddenByUser ? <EyeOffIcon /> : <EyeIcon />}
        </NavbarIcon>
        <Spacer width={1} height={0} />
      </NavbarItemContainer>
    );
  };

  if (links.length === 1) {
    return <Item navItem={item.navItemRedesign} label={item.label} indent={1} header={true} />;
  } else {
    return (
      <>
        <Item
          navItem={item.navItemRedesign}
          label={
            <Accordion
              padded={false}
              caretSize='md'
              titleGapSize={Spacing[2]}
              overrideExpanded={expanded}
              title={
                <>
                  <Paragraph color='primary' size='sm'>
                    {item.label}
                  </Paragraph>
                </>
              }
              onTitleClick={() => setExpanded(!expanded)}
            />
          }
          indent={1}
          header={true}
        />
        {expanded &&
          links.map((link) => {
            return (
              <Item
                key={`navbar-edit__subsection--${link.navItemRedesign}`}
                navItem={link.navItemRedesign}
                label={link.label}
                indent={5}
                sectionHidden={userHiddenIds.includes(item.navItemRedesign)}
              />
            );
          })}
      </>
    );
  }
};

export default EditModeNavbarItem;

const StyledLabelContainer = styled.div`
  width: 100%;
`;
